import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, X, LogOut, RefreshCw, Upload, Trash2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl, adminToken, adminHeaders } from "@/lib/api";
import SEO from "@/components/SEO";
import RichTextEditor from "@/components/RichTextEditor";

const VIEW_TABS = [
  { id: "submissions", label: "Submissions" },
  { id: "reel", label: "Homepage Reel" },
  { id: "sponsorship", label: "Sponsorship" },
  { id: "chronicle", label: "Chronicle" },
  { id: "personalisation", label: "Personalisation" },
];

const TABS = [
  { id: "pending", label: "Pending" },
  { id: "approved", label: "Approved" },
  { id: "rejected", label: "Rejected" },
];

const KIND_TABS = [
  { id: "all", label: "All" },
  { id: "artwork", label: "Artwork" },
  { id: "sighting", label: "Sightings" },
];

export default function Admin() {
  const nav = useNavigate();
  const [view, setView] = useState("submissions");
  const [tab, setTab] = useState("pending");
  const [kindTab, setKindTab] = useState("all");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!adminToken.get()) {
      nav("/admin/login");
      return;
    }
    load(tab, kindTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, kindTab]);

  const load = async (status, kind) => {
    setLoading(true);
    try {
      const params = { status };
      if (kind && kind !== "all") params.kind = kind;
      const r = await api.get("/admin/submissions", {
        params,
        headers: adminHeaders(),
      });
      setItems(r.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        adminToken.clear();
        nav("/admin/login");
      } else {
        toast.error("Couldn't load submissions");
      }
    } finally {
      setLoading(false);
    }
  };

  const act = async (id, kind) => {
    try {
      await api.post(`/admin/submissions/${id}/${kind}`, null, {
        headers: adminHeaders(),
      });
      setItems((arr) => arr.filter((it) => it.id !== id));
      toast.success(kind === "approve" ? "Approved ✦ Designed For Modern Leisure" : "Rejected");
    } catch {
      toast.error("Action failed");
    }
  };

  const logout = () => {
    adminToken.clear();
    nav("/admin/login");
  };

  return (
    <div data-testid="admin-page">
      <SEO title="Admin" path="/admin" noindex />
      <section className="border-b-2 border-[#1A3626] bg-[#F9F9F4] py-10">
        <div className="mx-auto flex max-w-[1400px] flex-wrap items-end justify-between gap-6 px-6 md:px-10">
          <div>
            <div className="font-display text-xs font-bold uppercase tracking-[0.3em]">
              Curator control room
            </div>
            <h1 className="font-display mt-2 text-4xl font-black tracking-tighter sm:text-5xl">
              Submissions.
            </h1>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => load(tab, kindTab)}
              className="wc-btn wc-btn-outline"
              data-testid="admin-refresh"
            >
              <RefreshCw className="mr-2 h-4 w-4" strokeWidth={2.5} /> Refresh
            </button>
            <button onClick={logout} className="wc-btn" data-testid="admin-logout">
              <LogOut className="mr-2 h-4 w-4" strokeWidth={2.5} /> Log out
            </button>
          </div>
        </div>
      </section>

      {/* Top-level view switcher: Submissions vs Homepage Reel */}
      <section className="border-b-2 border-[#1A3626] bg-[#F9F9F4]">
        <div className="mx-auto flex max-w-[1400px] items-center gap-2 px-6 py-4 md:px-10">
          {VIEW_TABS.map((v) => (
            <button
              key={v.id}
              onClick={() => setView(v.id)}
              className={`font-display border-2 border-[#1A3626] px-5 py-2.5 text-xs font-black uppercase tracking-[0.28em] transition-all ${
                view === v.id
                  ? "bg-[#1A3626] text-[#F9F9F4]"
                  : "bg-transparent text-[#1A3626] hover:bg-[#EBE7DD]"
              }`}
              data-testid={`admin-view-${v.id}`}
            >
              {v.label}
            </button>
          ))}
        </div>
      </section>

      {view === "submissions" && (
        <>
      <section className="sticky top-[73px] z-30 border-b-2 border-[#1A3626] bg-[#F9F9F4]/95 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1400px] flex-wrap items-center gap-3 px-6 py-4 md:gap-6 md:px-10">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-display mr-1 text-[10px] font-bold uppercase tracking-[0.3em] text-[#1A3626]/60">
              Status
            </span>
            {TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`font-display border-2 border-[#1A3626] px-4 py-2 text-xs font-bold uppercase tracking-widest transition-all ${
                  tab === t.id
                    ? "bg-[#1A3626] text-[#F9F9F4]"
                    : "bg-transparent hover:bg-[#EBE7DD]"
                }`}
                data-testid={`admin-tab-${t.id}`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="h-6 w-px bg-[#1A3626]/30 hidden md:block" aria-hidden />
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-display mr-1 text-[10px] font-bold uppercase tracking-[0.3em] text-[#1A3626]/60">
              Type
            </span>
            {KIND_TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setKindTab(t.id)}
                className={`font-display border-2 border-[#1A3626] px-4 py-2 text-xs font-bold uppercase tracking-widest transition-all ${
                  kindTab === t.id
                    ? "bg-[#93C065] text-[#0F0F0F]"
                    : "bg-transparent hover:bg-[#EBE7DD]"
                }`}
                data-testid={`admin-kind-tab-${t.id}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-10">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          {loading ? (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="aspect-square animate-pulse border-2 border-[#1A3626] bg-[#EBE7DD]"
                />
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="border-2 border-dashed border-[#1A3626] p-16 text-center font-display font-bold uppercase tracking-wide">
              Nothing here. Take a breather.
            </div>
          ) : (
            <div
              className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3"
              data-testid="admin-submissions-grid"
            >
              {items.map((s) => (
                <article
                  key={s.id}
                  className="wc-card overflow-hidden"
                  data-testid={`admin-sub-${s.id}`}
                >
                  <div className="aspect-[4/3] overflow-hidden bg-[#EBE7DD]">
                    <img
                      src={fileUrl(s.image_url)}
                      alt={s.title}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="border-t-2 border-[#1A3626] p-4">
                    <div className="mb-2 flex items-center gap-2">
                      <span
                        className={`font-display inline-flex items-center border-2 px-2 py-0.5 text-[9px] font-black uppercase tracking-widest ${
                          s.kind === "sighting"
                            ? "border-[#1A3626] bg-[#93C065] text-[#0F0F0F]"
                            : "border-[#1A3626] bg-[#F9F9F4] text-[#1A3626]"
                        }`}
                        data-testid={`admin-sub-kind-${s.id}`}
                      >
                        {s.kind === "sighting" ? "Sighting" : "Artwork"}
                      </span>
                      {s.kind === "sighting" && s.location && (
                        <span className="font-display text-[10px] font-bold uppercase tracking-widest text-[#1A3626]/70">
                          · {s.location}
                        </span>
                      )}
                    </div>
                    <div className="font-display text-base font-bold uppercase tracking-wide">
                      {s.title}
                    </div>
                    <div className="mt-1 text-xs text-[#1A3626]">
                      by {s.artist_name}
                      {s.handle ? ` · @${s.handle.replace(/^@/, "")}` : ""}
                    </div>
                    {s.description && (
                      <p className="mt-2 line-clamp-3 text-sm">{s.description}</p>
                    )}
                    <div className="mt-4 flex gap-2">
                      {tab !== "approved" && (
                        <button
                          onClick={() => act(s.id, "approve")}
                          className="wc-btn flex-1 bg-[#93C065] !text-[#0F0F0F]"
                          data-testid={`admin-approve-${s.id}`}
                        >
                          <Check className="mr-1 h-4 w-4" strokeWidth={3} /> Approve
                        </button>
                      )}
                      {tab !== "rejected" && (
                        <button
                          onClick={() => act(s.id, "reject")}
                          className="wc-btn wc-btn-outline flex-1"
                          data-testid={`admin-reject-${s.id}`}
                        >
                          <X className="mr-1 h-4 w-4" strokeWidth={3} /> Reject
                        </button>
                      )}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>
        </>
      )}

      {view === "reel" && <ReelAdmin />}

      {view === "sponsorship" && <SponsorshipAdmin />}

      {view === "chronicle" && <ChronicleAdmin />}

      {view === "personalisation" && <PersonalisationAdmin />}
    </div>
  );
}

/* =====================================================================
   HOMEPAGE REEL ADMIN — upload, reorder, toggle, delete
   ===================================================================== */
function ReelAdmin() {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [label, setLabel] = useState("");
  const [category, setCategory] = useState("damp");
  const [linkUrl, setLinkUrl] = useState("");
  const [file, setFile] = useState(null);
  const [mobileFile, setMobileFile] = useState(null);
  const [order, setOrder] = useState(0);
  const [uploading, setUploading] = useState(false);

  const CATS = [
    { id: "hero", label: "Hero (Top of Homepage)" },
    { id: "damp", label: "Mainline" },
    { id: "travel", label: "Travel" },
    { id: "everyday", label: "Everyday Life" },
    { id: "pub", label: "Pub" },
    { id: "sports", label: "Sports & Leisure" },
    { id: "dadlife", label: "Dad Life" },
    { id: "home", label: "Home" },
    { id: "coming", label: "Future Divisions" },
  ];

  const counts = {
    hero: photos.filter((p) => p.category === "hero").length,
    damp: photos.filter((p) => p.category === "damp").length,
    travel: photos.filter((p) => p.category === "travel").length,
    everyday: photos.filter((p) => p.category === "everyday").length,
    pub: photos.filter((p) => p.category === "pub").length,
    sports: photos.filter((p) => p.category === "sports").length,
    dadlife: photos.filter((p) => p.category === "dadlife").length,
    home: photos.filter((p) => p.category === "home").length,
    coming: photos.filter((p) => p.category === "coming").length,
  };

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/homepage-photos", { headers: adminHeaders() });
      setPhotos(r.data || []);
    } catch {
      toast.error("Couldn't load reel photos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Choose an image first");
      return;
    }
    if (!label.trim()) {
      toast.error("Add a label");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("label", label.trim());
      fd.append("category", category);
      fd.append("order", String(order));
      fd.append("active", "true");
      if (linkUrl.trim()) fd.append("link_url", linkUrl.trim());
      if (mobileFile) fd.append("mobile_file", mobileFile);
      await api.post("/admin/homepage-photos", fd, {
        headers: { ...adminHeaders(), "Content-Type": "multipart/form-data" },
      });
      toast.success("Photo added to reel");
      setLabel("");
      setLinkUrl("");
      setFile(null);
      setMobileFile(null);
      setOrder(0);
      document.getElementById("reel-file-input").value = "";
      const mInput = document.getElementById("reel-mobile-file-input");
      if (mInput) mInput.value = "";
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  // Inline editor for changing a photo's link_url after upload.
  const updateLinkUrl = async (p, newUrl) => {
    if ((p.link_url || "") === newUrl) return;
    try {
      const fd = new FormData();
      fd.append("link_url", newUrl);
      await api.patch(`/admin/homepage-photos/${p.id}`, fd, {
        headers: { ...adminHeaders(), "Content-Type": "multipart/form-data" },
      });
      toast.success(newUrl ? "Link updated" : "Link cleared");
      refresh();
    } catch {
      toast.error("Link update failed");
    }
  };

  const toggleActive = async (p) => {
    try {
      const fd = new FormData();
      fd.append("active", String(!p.active));
      await api.patch(`/admin/homepage-photos/${p.id}`, fd, {
        headers: { ...adminHeaders(), "Content-Type": "multipart/form-data" },
      });
      refresh();
    } catch {
      toast.error("Toggle failed");
    }
  };

  const updateOrder = async (p, newOrder) => {
    try {
      const fd = new FormData();
      fd.append("order", String(newOrder));
      await api.patch(`/admin/homepage-photos/${p.id}`, fd, {
        headers: { ...adminHeaders(), "Content-Type": "multipart/form-data" },
      });
      refresh();
    } catch {
      toast.error("Reorder failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this photo from the reel?")) return;
    try {
      await api.delete(`/admin/homepage-photos/${id}`, { headers: adminHeaders() });
      setPhotos((arr) => arr.filter((p) => p.id !== id));
      toast.success("Removed");
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <section className="py-10">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        {/* Upload form */}
        <form
          onSubmit={onUpload}
          className="wc-card grid grid-cols-1 gap-4 p-6 md:grid-cols-2 lg:grid-cols-[1.1fr_1.4fr_1.2fr_0.6fr_auto] lg:items-end"
          data-testid="reel-upload-form"
        >
          <div>
            <label className="font-display block text-[10px] font-bold uppercase tracking-[0.3em] text-[#1A3626]/70">
              {category === "hero" ? "Desktop image (16:9, ~2400×1350)" : "Image (max 10MB)"}
            </label>
            <input
              id="reel-file-input"
              type="file"
              accept="image/*"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="mt-2 block w-full text-sm"
              data-testid="reel-upload-file"
            />
            {category === "hero" && (
              <div className="mt-3">
                <label className="font-display block text-[10px] font-bold uppercase tracking-[0.3em] text-[#1A3626]/70">
                  Mobile image (4:5 portrait, ~1600×2000) — optional
                </label>
                <input
                  id="reel-mobile-file-input"
                  type="file"
                  accept="image/*"
                  onChange={(e) => setMobileFile(e.target.files?.[0] || null)}
                  className="mt-2 block w-full text-sm"
                  data-testid="reel-upload-mobile-file"
                />
                <p className="mt-1 text-[10px] uppercase tracking-[0.22em] text-[#1A3626]/50">
                  Falls back to desktop image if blank
                </p>
              </div>
            )}
          </div>
          <div>
            <label className="font-display block text-[10px] font-bold uppercase tracking-[0.3em] text-[#1A3626]/70">
              Label
            </label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Skegness Takeaway"
              className="mt-2 w-full border-2 border-[#1A3626] bg-[#F9F9F4] px-3 py-2 font-display text-sm"
              data-testid="reel-upload-label"
            />
          </div>
          <div>
            <label className="font-display block text-[10px] font-bold uppercase tracking-[0.3em] text-[#1A3626]/70">
              Reel
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="mt-2 w-full border-2 border-[#1A3626] bg-[#F9F9F4] px-3 py-2 font-display text-sm"
              data-testid="reel-upload-category"
            >
              {CATS.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label} ({counts[c.id]}/10)
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="font-display block text-[10px] font-bold uppercase tracking-[0.3em] text-[#1A3626]/70">
              Order
            </label>
            <input
              type="number"
              value={order}
              onChange={(e) => setOrder(Number(e.target.value) || 0)}
              className="mt-2 w-24 border-2 border-[#1A3626] bg-[#F9F9F4] px-3 py-2 font-display text-sm"
              data-testid="reel-upload-order"
            />
          </div>
          <button
            type="submit"
            disabled={uploading}
            className="wc-btn"
            data-testid="reel-upload-submit"
          >
            <Upload className="mr-2 h-4 w-4" strokeWidth={2.5} />
            {uploading ? "Uploading…" : "Add to reel"}
          </button>
          {/* Link URL spans the full row */}
          <div className="md:col-span-2 lg:col-span-5">
            <label className="font-display block text-[10px] font-bold uppercase tracking-[0.3em] text-[#1A3626]/70">
              Link URL (optional) — where this photo should send shoppers
            </label>
            <input
              type="text"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="/shop/abc123  ·  /collections/skegness  ·  https://…"
              className="mt-2 w-full border-2 border-[#1A3626] bg-[#F9F9F4] px-3 py-2 font-display text-sm"
              data-testid="reel-upload-link"
            />
          </div>
        </form>

        <div className="font-display mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-[10px] font-bold uppercase tracking-[0.3em] text-[#1A3626]/70">
          {CATS.map((c) => (
            <span key={c.id} data-testid={`reel-count-${c.id}`}>
              {c.label} · {counts[c.id]}/50
            </span>
          ))}
        </div>

        {/* Photo grid */}
        {loading ? (
          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="aspect-[3/4] animate-pulse border-2 border-[#1A3626] bg-[#EBE7DD]"
              />
            ))}
          </div>
        ) : photos.length === 0 ? (
          <div className="mt-6 border-2 border-dashed border-[#1A3626] p-16 text-center font-display font-bold uppercase tracking-wide">
            No reel photos yet. Upload the first one above.
          </div>
        ) : (
          <div
            className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-3 lg:grid-cols-4"
            data-testid="reel-photo-grid"
          >
            {photos.map((p) => (
              <article
                key={p.id}
                className={`wc-card overflow-hidden ${!p.active ? "opacity-50" : ""}`}
                data-testid={`reel-photo-${p.id}`}
              >
                <div className="relative aspect-[3/4] overflow-hidden bg-[#EBE7DD]">
                  <img
                    src={fileUrl(p.image_url)}
                    alt={p.label}
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                  <div className="font-display absolute left-2 top-2 inline-flex items-center gap-1 border-2 border-[#0F0F0F] bg-[#F9F9F4] px-2 py-1 text-[9px] font-black uppercase tracking-widest text-[#0F0F0F]">
                    #{p.order}
                  </div>
                </div>
                <div className="border-t-2 border-[#1A3626] p-3">
                  {(() => {
                    const CAT_STYLES = {
                      hero: "border-[#93C065] bg-[#93C065] text-[#0F0F0F]",
                      damp: "border-[#1A3626] bg-[#1A3626] text-[#F9F9F4]",
                      travel: "border-[#1A3626] bg-[#93C065] text-[#0F0F0F]",
                      everyday: "border-[#1A3626] bg-[#F4F2D9] text-[#1A3626]",
                      pub: "border-[#1A3626] bg-[#EBE7DD] text-[#1A3626]",
                      sports: "border-[#1A3626] bg-[#F9F9F4] text-[#1A3626]",
                      dadlife: "border-[#1A3626] bg-[#93C065] text-[#0F0F0F]",
                      home: "border-[#1A3626] bg-[#F4F2D9] text-[#1A3626]",
                      coming: "border-[#1A3626] bg-[#0F0F0F] text-[#93C065]",
                    };
                    const catStyle =
                      CAT_STYLES[p.category] ||
                      "border-[#1A3626] bg-[#F4F2D9] text-[#1A3626]";
                    const catLabel =
                      CATS.find((c) => c.id === p.category)?.label ||
                      p.category ||
                      "Uncategorized";
                    return (
                      <div
                        className={`font-display mb-2 inline-flex items-center border-2 px-2 py-0.5 text-[9px] font-black uppercase tracking-widest ${catStyle}`}
                        data-testid={`reel-photo-cat-${p.id}`}
                      >
                        {catLabel}
                      </div>
                    );
                  })()}
                  <div className="font-display text-xs font-bold uppercase tracking-widest">
                    {p.label}
                  </div>
                  <div className="mt-3">
                    <label className="font-display block text-[9px] font-bold uppercase tracking-[0.28em] text-[#1A3626]/60">
                      Link URL
                    </label>
                    <input
                      type="text"
                      defaultValue={p.link_url || ""}
                      onBlur={(e) => updateLinkUrl(p, e.target.value.trim())}
                      placeholder="/shop/abc123 · /collections/… · https://…"
                      className="mt-1 w-full border-2 border-[#1A3626] bg-[#F9F9F4] px-2 py-1.5 font-display text-[11px]"
                      data-testid={`reel-photo-link-${p.id}`}
                    />
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <input
                      type="number"
                      defaultValue={p.order}
                      onBlur={(e) => {
                        const v = Number(e.target.value);
                        if (v !== p.order) updateOrder(p, v);
                      }}
                      className="w-16 border-2 border-[#1A3626] bg-[#F9F9F4] px-2 py-1 text-xs"
                      title="Order"
                      data-testid={`reel-photo-order-${p.id}`}
                    />
                    <button
                      onClick={() => toggleActive(p)}
                      className="wc-btn wc-btn-outline flex-1 !py-2 !text-[10px]"
                      data-testid={`reel-photo-toggle-${p.id}`}
                    >
                      {p.active ? (
                        <>
                          <EyeOff className="mr-1 h-3 w-3" strokeWidth={2.6} /> Hide
                        </>
                      ) : (
                        <>
                          <Eye className="mr-1 h-3 w-3" strokeWidth={2.6} /> Show
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => remove(p.id)}
                      className="wc-btn !bg-[#1A3626] !py-2 !px-2 !text-[10px]"
                      data-testid={`reel-photo-delete-${p.id}`}
                      title="Delete"
                    >
                      <Trash2 className="h-3.5 w-3.5" strokeWidth={2.6} />
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}



/* =====================================================================
   SPONSORSHIP ADMIN — review underdog pitches submitted via /sponsorship
   ===================================================================== */
const PITCH_STATUS_TABS = [
  { id: "new", label: "New" },
  { id: "reviewed", label: "Reviewed" },
  { id: "archived", label: "Archived" },
  { id: "all", label: "All" },
];

function SponsorshipAdmin() {
  const [pitches, setPitches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusTab, setStatusTab] = useState("new");

  const refresh = async () => {
    setLoading(true);
    try {
      const params = statusTab === "all" ? {} : { status: statusTab };
      const r = await api.get("/admin/sponsorship", {
        headers: adminHeaders(),
        params,
      });
      setPitches(r.data || []);
    } catch {
      toast.error("Couldn't load pitches");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusTab]);

  const setStatus = async (id, status) => {
    try {
      const fd = new FormData();
      fd.append("status", status);
      await api.patch(`/admin/sponsorship/${id}`, fd, {
        headers: { ...adminHeaders(), "Content-Type": "multipart/form-data" },
      });
      toast.success(`Marked ${status}`);
      refresh();
    } catch {
      toast.error("Couldn't update pitch");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this pitch permanently?")) return;
    try {
      await api.delete(`/admin/sponsorship/${id}`, { headers: adminHeaders() });
      toast.success("Pitch deleted");
      setPitches((arr) => arr.filter((p) => p.id !== id));
    } catch {
      toast.error("Couldn't delete pitch");
    }
  };

  return (
    <section className="py-10" data-testid="sponsorship-admin">
      <div className="mx-auto max-w-[1100px] px-6 md:px-10">
        <div className="mb-6 flex flex-wrap items-center gap-2">
          <span className="font-display mr-1 text-[10px] font-bold uppercase tracking-[0.3em] text-[#1A3626]/60">
            Status
          </span>
          {PITCH_STATUS_TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setStatusTab(t.id)}
              className={`font-display border-2 border-[#1A3626] px-4 py-2 text-xs font-bold uppercase tracking-widest transition-all ${
                statusTab === t.id
                  ? "bg-[#1A3626] text-[#F9F9F4]"
                  : "bg-transparent hover:bg-[#EBE7DD]"
              }`}
              data-testid={`pitch-status-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
          <button
            onClick={refresh}
            className="font-display ml-auto border-2 border-[#1A3626] px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-[#EBE7DD]"
            data-testid="pitch-refresh"
          >
            <RefreshCw className="mr-2 inline h-3.5 w-3.5" strokeWidth={2.5} />
            Refresh
          </button>
        </div>

        {loading ? (
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="h-40 animate-pulse border-2 border-[#1A3626] bg-[#EBE7DD]"
              />
            ))}
          </div>
        ) : pitches.length === 0 ? (
          <div className="border-2 border-dashed border-[#1A3626] p-16 text-center font-display font-bold uppercase tracking-wide">
            No pitches in this bucket.
          </div>
        ) : (
          <div className="space-y-5" data-testid="pitches-list">
            {pitches.map((p) => (
              <article
                key={p.id}
                className="border-2 border-[#1A3626] bg-[#F9F9F4] p-6"
                data-testid={`pitch-${p.id}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="font-display text-[10px] font-bold uppercase tracking-[0.32em] text-[#93C065]">
                      {p.category}
                    </div>
                    <h3 className="font-display mt-2 text-2xl font-black uppercase tracking-tight text-[#1A3626]">
                      {p.team_name}
                    </h3>
                    <div className="mt-1 text-sm text-[#1A3626]/80">
                      {p.contact_name} ·{" "}
                      <a
                        href={`mailto:${p.email}`}
                        className="underline decoration-[#93C065] decoration-2 underline-offset-2 hover:text-[#1A3626]"
                      >
                        {p.email}
                      </a>
                    </div>
                  </div>
                  <span
                    className={`font-display border-2 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.25em] ${
                      p.status === "new"
                        ? "border-[#93C065] bg-[#93C065] text-[#0F0F0F]"
                        : p.status === "reviewed"
                        ? "border-[#1A3626] bg-[#1A3626] text-[#F9F9F4]"
                        : "border-[#1A3626]/40 text-[#1A3626]/60"
                    }`}
                  >
                    {p.status}
                  </span>
                </div>
                <p className="mt-4 whitespace-pre-line text-sm leading-relaxed text-[#1A3626] md:text-base">
                  {p.story}
                </p>
                <div className="mt-5 flex flex-wrap items-center gap-2 border-t border-[#1A3626]/15 pt-4 text-xs text-[#1A3626]/60">
                  <span>{new Date(p.created_at).toLocaleString()}</span>
                  <span className="ml-auto flex flex-wrap gap-2">
                    {p.status !== "reviewed" && (
                      <button
                        onClick={() => setStatus(p.id, "reviewed")}
                        className="font-display border-2 border-[#1A3626] bg-[#1A3626] px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-[#F9F9F4] transition-all hover:bg-transparent hover:text-[#1A3626]"
                        data-testid={`pitch-mark-reviewed-${p.id}`}
                      >
                        <Check className="mr-1 inline h-3 w-3" strokeWidth={2.6} />
                        Mark reviewed
                      </button>
                    )}
                    {p.status !== "archived" && (
                      <button
                        onClick={() => setStatus(p.id, "archived")}
                        className="font-display border-2 border-[#1A3626] px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-[#1A3626] transition-all hover:bg-[#1A3626] hover:text-[#F9F9F4]"
                        data-testid={`pitch-archive-${p.id}`}
                      >
                        Archive
                      </button>
                    )}
                    {p.status !== "new" && (
                      <button
                        onClick={() => setStatus(p.id, "new")}
                        className="font-display border-2 border-[#1A3626] px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-[#1A3626] transition-all hover:bg-[#EBE7DD]"
                        data-testid={`pitch-reopen-${p.id}`}
                      >
                        Reopen
                      </button>
                    )}
                    <button
                      onClick={() => remove(p.id)}
                      className="font-display border-2 border-[#1A3626] px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-[#1A3626] transition-all hover:bg-red-600 hover:text-[#F9F9F4]"
                      data-testid={`pitch-delete-${p.id}`}
                    >
                      <Trash2 className="h-3 w-3" strokeWidth={2.6} />
                    </button>
                  </span>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}


/* =====================================================================
   THE CABBAGE CHRONICLE — Admin tab. List, create, edit, publish, delete.
   ===================================================================== */
const POST_STATUS_TABS = [
  { id: "all", label: "All" },
  { id: "draft", label: "Drafts" },
  { id: "published", label: "Published" },
];

const EMPTY_POST = {
  id: null,
  title: "",
  slug: "",
  excerpt: "",
  body_html: "",
  pull_quote: "",
  hero_image_url: "",
  hero_image_alt: "",
  category: "field-notes",
  author: "The Curator",
  status: "draft",
};

function slugify(s) {
  return (s || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/[\s_]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 200);
}

function ChronicleAdmin() {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [statusTab, setStatusTab] = useState("all");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [slugTouched, setSlugTouched] = useState(false);
  const [uploadingHero, setUploadingHero] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/chronicle/categories").then((r) => setCategories(r.data || []));
  }, []);

  const refresh = async () => {
    setLoading(true);
    try {
      const params = statusTab === "all" ? {} : { status: statusTab };
      const r = await api.get("/admin/chronicle", {
        headers: adminHeaders(),
        params,
      });
      setPosts(r.data || []);
    } catch {
      toast.error("Couldn't load posts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusTab]);

  const startNew = () => {
    setEditing({ ...EMPTY_POST });
    setSlugTouched(false);
  };

  const startEdit = (p) => {
    setEditing({ ...p, pull_quote: p.pull_quote || "" });
    setSlugTouched(true);
  };

  const onTitleChange = (v) => {
    setEditing((s) => {
      const next = { ...s, title: v };
      if (!slugTouched) next.slug = slugify(v);
      return next;
    });
  };

  const onHeroUpload = async (file) => {
    if (!file) return;
    setUploadingHero(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/chronicle/upload-image", fd, {
        headers: { ...adminHeaders(), "Content-Type": "multipart/form-data" },
      });
      setEditing((s) => ({ ...s, hero_image_url: r.data?.url || "" }));
      toast.success("Hero image uploaded");
    } catch {
      toast.error("Hero upload failed");
    } finally {
      setUploadingHero(false);
    }
  };

  const save = async (newStatus) => {
    if (!editing.title.trim()) {
      toast.error("Add a title");
      return;
    }
    const payload = {
      title: editing.title.trim(),
      slug: editing.slug || slugify(editing.title),
      excerpt: editing.excerpt || "",
      body_html: editing.body_html || "",
      pull_quote: (editing.pull_quote || "").trim() || null,
      hero_image_url: editing.hero_image_url || null,
      hero_image_alt: editing.hero_image_alt || null,
      category: editing.category,
      author: editing.author.trim() || "The Curator",
      status: newStatus || editing.status,
    };
    setSaving(true);
    try {
      let r;
      if (editing.id) {
        r = await api.patch(`/admin/chronicle/${editing.id}`, payload, {
          headers: adminHeaders(),
        });
      } else {
        r = await api.post("/admin/chronicle", payload, {
          headers: adminHeaders(),
        });
      }
      setEditing({ ...r.data, pull_quote: r.data.pull_quote || "" });
      setSlugTouched(true);
      toast.success(
        payload.status === "published" ? "Published" : "Draft saved"
      );
      refresh();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't save post");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this post permanently?")) return;
    try {
      await api.delete(`/admin/chronicle/${id}`, { headers: adminHeaders() });
      toast.success("Post deleted");
      setPosts((arr) => arr.filter((p) => p.id !== id));
    } catch {
      toast.error("Couldn't delete post");
    }
  };

  if (editing) {
    return (
      <ChronicleEditor
        post={editing}
        setPost={setEditing}
        categories={categories}
        onTitleChange={onTitleChange}
        setSlugTouched={setSlugTouched}
        uploadingHero={uploadingHero}
        onHeroUpload={onHeroUpload}
        save={save}
        saving={saving}
        onClose={() => setEditing(null)}
      />
    );
  }

  return (
    <section className="py-10" data-testid="chronicle-admin">
      <div className="mx-auto max-w-[1100px] px-6 md:px-10">
        <div className="mb-6 flex flex-wrap items-center gap-2">
          <span className="font-display mr-1 text-[10px] font-bold uppercase tracking-[0.3em] text-[#1A3626]/60">
            Status
          </span>
          {POST_STATUS_TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setStatusTab(t.id)}
              className={`font-display border-2 border-[#1A3626] px-4 py-2 text-xs font-bold uppercase tracking-widest transition-all ${
                statusTab === t.id
                  ? "bg-[#1A3626] text-[#F9F9F4]"
                  : "bg-transparent hover:bg-[#EBE7DD]"
              }`}
              data-testid={`post-status-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
          <button
            onClick={startNew}
            className="font-display ml-auto border-2 border-[#1A3626] bg-[#1A3626] px-5 py-2 text-xs font-black uppercase tracking-widest text-[#F9F9F4] transition-all hover:-translate-y-0.5 hover:bg-transparent hover:text-[#1A3626]"
            data-testid="chronicle-new-post"
          >
            New post
          </button>
        </div>

        {loading ? (
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="h-28 animate-pulse border-2 border-[#1A3626] bg-[#EBE7DD]"
              />
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div className="border-2 border-dashed border-[#1A3626] p-16 text-center font-display font-bold uppercase tracking-wide">
            No posts yet. Click &ldquo;New post&rdquo; to write the first dispatch.
          </div>
        ) : (
          <div className="space-y-4" data-testid="chronicle-posts-list">
            {posts.map((p) => (
              <article
                key={p.id}
                className="flex flex-col gap-4 border-2 border-[#1A3626] bg-[#F9F9F4] p-5 md:flex-row md:items-center md:gap-6 md:p-6"
                data-testid={`chronicle-row-${p.id}`}
              >
                <div className="aspect-[4/3] h-24 shrink-0 overflow-hidden border-2 border-[#1A3626] bg-[#EBE7DD] md:h-20">
                  {p.hero_image_url && (
                    <img
                      src={fileUrl(p.hero_image_url)}
                      alt=""
                      className="h-full w-full object-cover"
                    />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={`font-display border-2 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.25em] ${
                        p.status === "published"
                          ? "border-[#93C065] bg-[#93C065] text-[#0F0F0F]"
                          : "border-[#1A3626]/40 text-[#1A3626]/60"
                      }`}
                    >
                      {p.status}
                    </span>
                    <span className="font-display text-[10px] font-bold uppercase tracking-[0.25em] text-[#93C065]">
                      {categories.find((c) => c.key === p.category)?.label ||
                        p.category}
                    </span>
                  </div>
                  <h3 className="font-display mt-2 text-lg font-black uppercase leading-tight tracking-tight text-[#1A3626] md:text-xl">
                    {p.title}
                  </h3>
                  <div className="mt-1 text-xs text-[#1A3626]/60">
                    /{p.slug} ·{" "}
                    {p.published_at
                      ? new Date(p.published_at).toLocaleDateString()
                      : `updated ${new Date(p.updated_at).toLocaleDateString()}`}
                  </div>
                </div>
                <div className="flex shrink-0 gap-2">
                  <button
                    onClick={() => startEdit(p)}
                    className="font-display border-2 border-[#1A3626] px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest transition-all hover:bg-[#EBE7DD]"
                    data-testid={`chronicle-edit-${p.id}`}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => remove(p.id)}
                    className="font-display border-2 border-[#1A3626] px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest transition-all hover:bg-red-600 hover:text-[#F9F9F4]"
                    data-testid={`chronicle-delete-${p.id}`}
                  >
                    <Trash2 className="h-3 w-3" strokeWidth={2.6} />
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function ChronicleEditor({
  post,
  setPost,
  categories,
  onTitleChange,
  setSlugTouched,
  uploadingHero,
  onHeroUpload,
  save,
  saving,
  onClose,
}) {
  return (
    <section className="py-8" data-testid="chronicle-editor">
      <div className="mx-auto max-w-[1100px] px-6 md:px-10">
        <div className="mb-6 flex flex-wrap items-center gap-3">
          <button
            onClick={onClose}
            className="font-display border-2 border-[#1A3626] px-4 py-2 text-xs font-bold uppercase tracking-widest transition-all hover:bg-[#EBE7DD]"
            data-testid="chronicle-editor-back"
          >
            ← Back to list
          </button>
          <span className="font-display text-[10px] font-bold uppercase tracking-[0.3em] text-[#1A3626]/60">
            {post.id ? "Editing post" : "New post"}
          </span>
          <span className="ml-auto flex flex-wrap gap-2">
            <button
              onClick={() => save("draft")}
              disabled={saving}
              className="font-display border-2 border-[#1A3626] px-5 py-2 text-xs font-bold uppercase tracking-widest text-[#1A3626] transition-all hover:bg-[#EBE7DD] disabled:opacity-50"
              data-testid="chronicle-save-draft"
            >
              Save draft
            </button>
            <button
              onClick={() => save("published")}
              disabled={saving}
              className="font-display border-2 border-[#1A3626] bg-[#1A3626] px-5 py-2 text-xs font-black uppercase tracking-widest text-[#F9F9F4] transition-all hover:-translate-y-0.5 hover:bg-transparent hover:text-[#1A3626] disabled:opacity-50"
              data-testid="chronicle-publish"
            >
              {post.status === "published" ? "Update" : "Publish"}
            </button>
          </span>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[2fr_1fr]">
          <div className="space-y-5">
            <ChronicleField label="Title">
              <input
                type="text"
                value={post.title}
                onChange={(e) => onTitleChange(e.target.value)}
                placeholder="The Decline Of Proper Petrol Station Behaviour"
                className="chronicle-form-input font-display text-2xl font-black uppercase tracking-tight md:text-3xl"
                data-testid="chronicle-input-title"
              />
            </ChronicleField>

            <ChronicleField
              label="Excerpt / Standfirst"
              helper="One or two sentences that appear on the Chronicle index card."
            >
              <textarea
                value={post.excerpt}
                onChange={(e) =>
                  setPost((s) => ({ ...s, excerpt: e.target.value }))
                }
                maxLength={400}
                rows={3}
                placeholder="A field report from the M6 northbound services…"
                className="chronicle-form-input"
                data-testid="chronicle-input-excerpt"
              />
            </ChronicleField>

            <ChronicleField label="Body">
              <RichTextEditor
                value={post.body_html}
                onChange={(html) =>
                  setPost((s) => ({ ...s, body_html: html }))
                }
                placeholder="Start writing the dispatch…"
              />
            </ChronicleField>

            <ChronicleField
              label="Pull quote"
              helper="Optional. Renders large between paragraphs on the post page."
            >
              <textarea
                value={post.pull_quote}
                onChange={(e) =>
                  setPost((s) => ({ ...s, pull_quote: e.target.value }))
                }
                maxLength={600}
                rows={2}
                placeholder="We used to nod. Now we just buy crisps and avoid each other."
                className="chronicle-form-input"
                data-testid="chronicle-input-pullquote"
              />
            </ChronicleField>
          </div>

          <aside className="space-y-5">
            <div className="border-2 border-[#1A3626] bg-[#F9F9F4] p-5">
              <div className="font-display mb-3 text-[10px] font-black uppercase tracking-[0.3em] text-[#1A3626]/60">
                Hero image
              </div>
              {post.hero_image_url ? (
                <div className="space-y-3">
                  <div className="aspect-[4/3] overflow-hidden border-2 border-[#1A3626] bg-[#EBE7DD]">
                    <img
                      src={fileUrl(post.hero_image_url)}
                      alt=""
                      className="h-full w-full object-cover"
                    />
                  </div>
                  <input
                    type="text"
                    value={post.hero_image_alt || ""}
                    onChange={(e) =>
                      setPost((s) => ({ ...s, hero_image_alt: e.target.value }))
                    }
                    placeholder="Alt text (for screen readers)"
                    className="chronicle-form-input text-sm"
                    data-testid="chronicle-input-hero-alt"
                  />
                  <button
                    onClick={() =>
                      setPost((s) => ({
                        ...s,
                        hero_image_url: "",
                        hero_image_alt: "",
                      }))
                    }
                    className="font-display w-full border-2 border-[#1A3626] px-3 py-2 text-xs font-bold uppercase tracking-widest transition-all hover:bg-red-600 hover:text-[#F9F9F4]"
                  >
                    Remove image
                  </button>
                </div>
              ) : (
                <label className="flex cursor-pointer flex-col items-center gap-3 border-2 border-dashed border-[#1A3626] bg-[#F9F9F4] p-6 text-center transition-colors hover:bg-[#EBE7DD]">
                  <Upload className="h-6 w-6" strokeWidth={2.4} />
                  <span className="font-display text-xs font-bold uppercase tracking-widest">
                    {uploadingHero ? "Uploading…" : "Upload hero image"}
                  </span>
                  <span className="text-[10px] text-[#1A3626]/60">
                    JPEG / PNG / WebP · max 10MB
                  </span>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={uploadingHero}
                    onChange={(e) => onHeroUpload(e.target.files?.[0])}
                    data-testid="chronicle-hero-upload"
                  />
                </label>
              )}
            </div>

            <div className="border-2 border-[#1A3626] bg-[#F9F9F4] p-5">
              <div className="font-display mb-3 text-[10px] font-black uppercase tracking-[0.3em] text-[#1A3626]/60">
                Meta
              </div>
              <div className="space-y-4">
                <ChronicleField
                  label="Slug"
                  helper="URL path. Generated from title — edit if needed."
                >
                  <input
                    type="text"
                    value={post.slug}
                    onChange={(e) => {
                      setSlugTouched(true);
                      setPost((s) => ({ ...s, slug: slugify(e.target.value) }));
                    }}
                    className="chronicle-form-input text-sm"
                    data-testid="chronicle-input-slug"
                  />
                </ChronicleField>

                <ChronicleField label="Category">
                  <select
                    value={post.category}
                    onChange={(e) =>
                      setPost((s) => ({ ...s, category: e.target.value }))
                    }
                    className="chronicle-form-input text-sm"
                    data-testid="chronicle-input-category"
                  >
                    {categories.map((c) => (
                      <option key={c.key} value={c.key}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </ChronicleField>

                <ChronicleField label="Author">
                  <input
                    type="text"
                    value={post.author}
                    onChange={(e) =>
                      setPost((s) => ({ ...s, author: e.target.value }))
                    }
                    className="chronicle-form-input text-sm"
                    data-testid="chronicle-input-author"
                  />
                </ChronicleField>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <style>{`
        .chronicle-form-input{
          width:100%;background:#F9F9F4;border:2px solid #1A3626;
          padding:12px 14px;font-size:15px;color:#1A3626;font-family:inherit;
          outline:none;transition:box-shadow 150ms ease, transform 150ms ease;
        }
        .chronicle-form-input:focus{ box-shadow: 4px 4px 0 0 #93C065; transform: translate(-1px,-1px); }
        textarea.chronicle-form-input{ resize: vertical; min-height: 80px; line-height: 1.5; }
      `}</style>
    </section>
  );
}

function ChronicleField({ label, helper, children }) {
  return (
    <label className="block">
      <span className="font-display block text-[10px] font-black uppercase tracking-[0.3em] text-[#1A3626] md:text-xs">
        {label}
      </span>
      <div className="mt-2">{children}</div>
      {helper && (
        <span className="mt-2 block text-xs leading-relaxed text-[#1A3626]/60">
          {helper}
        </span>
      )}
    </label>
  );
}



/* =====================================================================
   PERSONALISATION ADMIN — manage personalisable products + review sessions
   ===================================================================== */
function PersonalisationAdmin() {
  const [products, setProducts] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [orders, setOrders] = useState([]);
  const [pane, setPane] = useState("queue"); // "queue" | "products" | "orders"
  const [loading, setLoading] = useState(true);
  const [shopifyProductId, setShopifyProductId] = useState("");
  const [title, setTitle] = useState("");
  const [designPrompt, setDesignPrompt] = useState("");
  const [defaultPrompt, setDefaultPrompt] = useState("");
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [faceLabel, setFaceLabel] = useState("Your mate");
  const [uploadInstructions, setUploadInstructions] = useState(
    "Use a clear, well-lit photo of just the face. Plain background works best."
  );
  const [baseFile, setBaseFile] = useState(null);
  const [creating, setCreating] = useState(false);
  const [printifyBlueprintId, setPrintifyBlueprintId] = useState("");
  const [printifyPrintProviderId, setPrintifyPrintProviderId] = useState("");
  const [printifyVariantIds, setPrintifyVariantIds] = useState("");
  const [printifyTemplateProductId, setPrintifyTemplateProductId] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [p, s, o] = await Promise.all([
        api.get("/admin/personalisable-products", { headers: adminHeaders() }),
        api.get("/admin/personalisation-sessions", { headers: adminHeaders() }),
        api.get("/admin/personalisation-orders", { headers: adminHeaders() }),
      ]);
      setProducts(p.data || []);
      setSessions(s.data || []);
      setOrders(o.data || []);
    } catch {
      toast.error("Couldn't load personalisation data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Pre-fill the default prompt so admin can submit without writing one.
    api
      .get("/personalisation/default-prompt")
      .then((r) => setDefaultPrompt(r.data?.prompt || ""))
      .catch(() => {});
  }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!shopifyProductId.trim() || !title.trim() || !baseFile) {
      toast.error("Shopify product ID, title and base image are required.");
      return;
    }
    setCreating(true);
    try {
      const fd = new FormData();
      fd.append("shopify_product_id", shopifyProductId.trim());
      fd.append("title", title.trim());
      // Only send a custom prompt if admin actually wrote one; otherwise the
      // backend applies the generic default.
      if (designPrompt.trim()) fd.append("design_prompt", designPrompt.trim());
      fd.append("face_label", faceLabel.trim() || "Your mate");
      fd.append("upload_instructions", uploadInstructions.trim());
      fd.append("active", "true");
      if (printifyBlueprintId.trim())
        fd.append("printify_blueprint_id", printifyBlueprintId.trim());
      if (printifyPrintProviderId.trim())
        fd.append("printify_print_provider_id", printifyPrintProviderId.trim());
      if (printifyVariantIds.trim())
        fd.append("printify_variant_ids", printifyVariantIds.trim());
      if (printifyTemplateProductId.trim())
        fd.append("printify_template_product_id", printifyTemplateProductId.trim());
      fd.append("base_design", baseFile);
      await api.post("/admin/personalisable-products", fd, {
        headers: { ...adminHeaders(), "Content-Type": "multipart/form-data" },
      });
      toast.success("Product marked as personalisable.");
      setShopifyProductId("");
      setTitle("");
      setDesignPrompt("");
      setBaseFile(null);
      setPrintifyBlueprintId("");
      setPrintifyPrintProviderId("");
      setPrintifyVariantIds("");
      setPrintifyTemplateProductId("");
      await load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't save product";
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (p) => {
    try {
      const fd = new FormData();
      fd.append("active", String(!p.active));
      await api.patch(`/admin/personalisable-products/${p.id}`, fd, {
        headers: { ...adminHeaders(), "Content-Type": "multipart/form-data" },
      });
      await load();
    } catch {
      toast.error("Couldn't update");
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Remove personalisation for "${p.title}"?`)) return;
    try {
      await api.delete(`/admin/personalisable-products/${p.id}`, {
        headers: adminHeaders(),
      });
      await load();
    } catch {
      toast.error("Couldn't delete");
    }
  };

  const queueOrder = async (sessionId) => {
    try {
      await api.post(
        "/admin/personalisation-orders",
        { session_id: sessionId },
        { headers: adminHeaders() }
      );
      toast.success("Queued for fulfilment.");
      await load();
      setPane("orders");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't queue";
      toast.error(msg);
    }
  };

  const updateOrderStatus = async (o, status) => {
    try {
      const fd = new FormData();
      fd.append("status", status);
      await api.patch(`/admin/personalisation-orders/${o.id}/status`, fd, {
        headers: { ...adminHeaders(), "Content-Type": "multipart/form-data" },
      });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update");
    }
  };

  const pushToPrintify = async (o) => {
    try {
      toast.loading("Uploading to Printify…", { id: `push-${o.id}` });
      await api.post(
        `/admin/personalisation-orders/${o.id}/push-to-printify`,
        null,
        { headers: adminHeaders(), timeout: 60000 }
      );
      toast.success("Uploaded to Printify media library.", { id: `push-${o.id}` });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Printify push failed", {
        id: `push-${o.id}`,
      });
    }
  };

  const deleteOrder = async (o) => {
    if (!window.confirm("Delete this fulfilment order?")) return;
    try {
      await api.delete(`/admin/personalisation-orders/${o.id}`, {
        headers: adminHeaders(),
      });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't delete");
    }
  };

  const approvedSessionsWithoutOrder = sessions.filter(
    (s) => s.status === "approved" && !orders.some((o) => o.session_id === s.id)
  );

  return (
    <section
      className="space-y-12 px-6 py-12 md:px-10 md:py-16"
      data-testid="personalisation-admin"
    >
      <header className="max-w-2xl">
        <div className="font-display text-[10px] font-bold uppercase tracking-[0.4em] text-[#93C065]">
          Personalisation
        </div>
        <h2 className="font-display mt-3 text-2xl font-black uppercase leading-[0.95] tracking-tight md:text-3xl">
          Concierge personalisation studio.
        </h2>
        <p className="mt-3 text-sm leading-relaxed text-[#1A3626]/80 md:text-[15px]">
          The <strong>Queue</strong> shows customer photo submissions waiting
          for a hand-crafted proof. <strong>Products</strong> shows every
          Shopify product that&rsquo;s personalisable —{" "}
          <em>new Shopify products auto-enable the moment a customer visits
          them</em>. Deactivate any you want to be standard-only.{" "}
          <strong>Orders</strong> handles paid orders being pushed to Printify.
        </p>
      </header>

      {/* Pane switcher */}
      <div className="flex flex-wrap items-center gap-2" data-testid="personalisation-pane-toggle">
        {[
          { id: "queue", label: `Queue (${sessions.filter((s) => s.status === "awaiting_admin_proof").length || sessions.length})` },
          { id: "products", label: `Products (${products.length})` },
          { id: "orders", label: `Orders (${orders.length})` },
        ].map((opt) => (
          <button
            key={opt.id}
            onClick={() => setPane(opt.id)}
            className={`font-display border-2 border-[#1A3626] px-4 py-2 text-[11px] font-black uppercase tracking-[0.3em] transition ${
              pane === opt.id
                ? "bg-[#1A3626] text-[#F9F9F4]"
                : "bg-transparent text-[#1A3626] hover:bg-[#EBE7DD]"
            }`}
            data-testid={`personalisation-pane-${opt.id}`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {pane === "queue" && (
        <ConciergeQueue sessions={sessions} onChange={load} />
      )}

      {pane === "products" && (
        <>

      {/* Create form */}
      <form
        onSubmit={create}
        className="grid gap-5 border-2 border-[#1A3626] bg-white p-5 md:p-7"
        data-testid="personalisable-create-form"
      >
        <div className="grid gap-5 md:grid-cols-2">
          <ChronicleField
            label="Shopify product ID"
            helper="The exact product ID used by /shop/{id}. Find it in the Shopify admin URL or product list."
          >
            <input
              value={shopifyProductId}
              onChange={(e) => setShopifyProductId(e.target.value)}
              className="w-full border-2 border-[#1A3626] bg-[#F9F9F4] px-3 py-2 text-sm"
              required
              data-testid="personalisable-shopify-id"
            />
          </ChronicleField>
          <ChronicleField label="Display title" helper="Shown to the customer in the modal.">
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full border-2 border-[#1A3626] bg-[#F9F9F4] px-3 py-2 text-sm"
              required
              data-testid="personalisable-title"
            />
          </ChronicleField>
        </div>

        <div className="grid gap-5 md:grid-cols-2">
          <ChronicleField label="Face label" helper='Shown next to the upload slot. e.g. "Your Dad", "Your mate".'>
            <input
              value={faceLabel}
              onChange={(e) => setFaceLabel(e.target.value)}
              className="w-full border-2 border-[#1A3626] bg-[#F9F9F4] px-3 py-2 text-sm"
              data-testid="personalisable-face-label"
            />
          </ChronicleField>
          <ChronicleField label="Upload instructions" helper="Customer-facing tip on what photo to provide.">
            <input
              value={uploadInstructions}
              onChange={(e) => setUploadInstructions(e.target.value)}
              className="w-full border-2 border-[#1A3626] bg-[#F9F9F4] px-3 py-2 text-sm"
              data-testid="personalisable-instructions"
            />
          </ChronicleField>
        </div>

        <ChronicleField label="Base design image" helper="The original product artwork (PNG/JPG, up to 16MB).">
          <input
            type="file"
            accept="image/*"
            onChange={(e) => setBaseFile(e.target.files?.[0] || null)}
            className="text-sm"
            required
            data-testid="personalisable-base-file"
          />
        </ChronicleField>

        {/* Advanced options — collapsed by default. Currently only used for
            Printify auto-fulfilment template fields (no AI in the flow). */}
        <div className="border-t border-[#1A3626]/20 pt-4">
          <button
            type="button"
            onClick={() => setAdvancedOpen((v) => !v)}
            className="font-display flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.3em] text-[#1A3626] hover:text-[#93C065]"
            data-testid="personalisable-advanced-toggle"
            aria-expanded={advancedOpen}
          >
            {advancedOpen ? "▾" : "▸"} Advanced options{" "}
            <span className="text-[#1A3626]/50 normal-case tracking-normal">
              (Printify template for auto-fulfilment)
            </span>
          </button>
          {advancedOpen && (
            <div className="mt-5 space-y-5" data-testid="personalisable-advanced-pane">
              {/* Printify template config */}
              <div className="border-2 border-dashed border-[#1A3626]/40 p-4">
                <div className="font-display text-[10px] font-bold uppercase tracking-[0.4em] text-[#93C065]">
                  Printify Template{" "}
                  <span className="text-[#1A3626]/50">(for auto-fulfilment)</span>
                </div>
                <p className="mt-2 text-xs leading-relaxed text-[#1A3626]/70">
                  Fill these once per design and the Push-to-Printify button
                  in the Orders pane will eventually auto-create products +
                  submit orders for you. Skip for now — uploads to Printify
                  still work as a manual handoff.
                </p>
                <div className="mt-3 grid gap-4 md:grid-cols-3">
                  <ChronicleField label="Blueprint ID" helper="e.g. 5 (Heavy Cotton Tee)">
                    <input
                      value={printifyBlueprintId}
                      onChange={(e) => setPrintifyBlueprintId(e.target.value)}
                      className="w-full border-2 border-[#1A3626] bg-[#F9F9F4] px-3 py-2 text-sm"
                      type="number"
                      inputMode="numeric"
                      data-testid="personalisable-printify-blueprint"
                    />
                  </ChronicleField>
                  <ChronicleField label="Print provider ID" helper="e.g. 99 (Printify Choice)">
                    <input
                      value={printifyPrintProviderId}
                      onChange={(e) => setPrintifyPrintProviderId(e.target.value)}
                      className="w-full border-2 border-[#1A3626] bg-[#F9F9F4] px-3 py-2 text-sm"
                      type="number"
                      inputMode="numeric"
                      data-testid="personalisable-printify-provider"
                    />
                  </ChronicleField>
                  <ChronicleField label="Variant IDs" helper="Comma-separated">
                    <input
                      value={printifyVariantIds}
                      onChange={(e) => setPrintifyVariantIds(e.target.value)}
                      className="w-full border-2 border-[#1A3626] bg-[#F9F9F4] px-3 py-2 text-sm"
                      data-testid="personalisable-printify-variants"
                    />
                  </ChronicleField>
                </div>
                <div className="mt-3">
                  <ChronicleField
                    label="Existing Printify product ID (alternative)"
                    helper="If you've configured this product on Printify already, paste its ID here."
                  >
                    <input
                      value={printifyTemplateProductId}
                      onChange={(e) => setPrintifyTemplateProductId(e.target.value)}
                      className="w-full border-2 border-[#1A3626] bg-[#F9F9F4] px-3 py-2 text-sm"
                      data-testid="personalisable-printify-template"
                    />
                  </ChronicleField>
                </div>
              </div>
            </div>
          )}
        </div>

        <button
          type="submit"
          disabled={creating}
          className="wc-btn inline-flex items-center gap-2 self-start disabled:opacity-50"
          data-testid="personalisable-submit"
        >
          {creating ? "Saving…" : "Mark As Personalisable"}
        </button>
      </form>

      {/* Existing products */}
      <div>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="font-display text-[10px] font-bold uppercase tracking-[0.4em] text-[#1A3626]/65">
            Active personalisable products ({products.length})
          </div>
          <button
            onClick={async () => {
              try {
                toast.loading("Syncing from Shopify…", { id: "sync" });
                const r = await api.post(
                  "/admin/personalisable-products/sync-from-shopify",
                  null,
                  { headers: adminHeaders(), timeout: 60000 }
                );
                const added = r.data?.total_added || 0;
                toast.success(
                  added > 0
                    ? `Added ${added} product${added > 1 ? "s" : ""} from Shopify.`
                    : "Already synced — no new products to add.",
                  { id: "sync" }
                );
                await load();
              } catch (err) {
                toast.error(err?.response?.data?.detail || "Sync failed", {
                  id: "sync",
                });
              }
            }}
            className="font-display border-2 border-[#1A3626] bg-transparent px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.28em] hover:bg-[#1A3626] hover:text-[#F9F9F4]"
            data-testid="sync-shopify-btn"
          >
            Sync from Shopify
          </button>
        </div>
        {loading ? (
          <div className="text-sm text-[#1A3626]/70">Loading…</div>
        ) : products.length === 0 ? (
          <div className="border-2 border-dashed border-[#1A3626] p-6 text-sm text-[#1A3626]/70">
            No personalisable products yet. Add one above to start the flow.
          </div>
        ) : (
          <ul className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {products.map((p) => (
              <li
                key={p.id}
                className="flex gap-4 border-2 border-[#1A3626] bg-white p-4"
                data-testid={`personalisable-row-${p.id}`}
              >
                <img
                  src={fileUrl(p.base_design_url)}
                  alt={p.title}
                  className="h-24 w-24 shrink-0 border-2 border-[#1A3626] object-contain"
                />
                <div className="flex flex-1 flex-col">
                  <div className="font-display text-base font-black uppercase tracking-tight">
                    {p.title}
                  </div>
                  <div className="text-xs text-[#1A3626]/60">
                    Shopify ID: {p.shopify_product_id}
                  </div>
                  <div className="mt-1 text-xs text-[#1A3626]/60">
                    Face label: {p.face_label}
                  </div>
                  <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-[#1A3626]/80">
                    {p.design_prompt}
                  </p>
                  {(p.printify_blueprint_id || p.printify_template_product_id) && (
                    <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[10px] uppercase tracking-[0.2em] text-[#93C065]">
                      {p.printify_blueprint_id && (
                        <span data-testid={`pi-bp-${p.id}`}>BP {p.printify_blueprint_id}</span>
                      )}
                      {p.printify_print_provider_id && (
                        <span>PV {p.printify_print_provider_id}</span>
                      )}
                      {p.printify_variant_ids?.length ? (
                        <span>VAR ×{p.printify_variant_ids.length}</span>
                      ) : null}
                      {p.printify_template_product_id && (
                        <span>TPL {p.printify_template_product_id.slice(0, 8)}</span>
                      )}
                    </div>
                  )}
                  <div className="mt-3 flex items-center gap-3">
                    <button
                      onClick={() => toggleActive(p)}
                      className="font-display border-2 border-[#1A3626] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.25em] hover:bg-[#1A3626] hover:text-[#F9F9F4]"
                      data-testid={`personalisable-toggle-${p.id}`}
                    >
                      {p.active ? "Deactivate" : "Activate"}
                    </button>
                    <button
                      onClick={() => remove(p)}
                      className="font-display border-2 border-red-700 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.25em] text-red-700 hover:bg-red-700 hover:text-white"
                      data-testid={`personalisable-delete-${p.id}`}
                    >
                      Delete
                    </button>
                    {!p.active && (
                      <span className="text-[10px] uppercase tracking-[0.25em] text-[#1A3626]/50">
                        Inactive
                      </span>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Concierge queue lives in its own "Queue" pane now */}
        </>
      )}

      {pane === "orders" && (
        <PersonalisationOrdersPane
          orders={orders}
          sessions={sessions}
          approvedSessionsWithoutOrder={approvedSessionsWithoutOrder}
          onQueue={queueOrder}
          onUpdateStatus={updateOrderStatus}
          onPush={pushToPrintify}
          onDelete={deleteOrder}
        />
      )}
    </section>
  );
}

/* ---------- Personalisation Orders pane ---------- */
function PersonalisationOrdersPane({
  orders,
  sessions,
  approvedSessionsWithoutOrder,
  onQueue,
  onUpdateStatus,
  onPush,
  onDelete,
}) {
  const sessionById = Object.fromEntries(sessions.map((s) => [s.id, s]));
  return (
    <div className="space-y-12" data-testid="orders-pane">
      {/* Approved sessions waiting to be queued */}
      <div>
        <div className="font-display mb-4 text-[10px] font-bold uppercase tracking-[0.4em] text-[#1A3626]/65">
          Approved sessions awaiting fulfilment ({approvedSessionsWithoutOrder.length})
        </div>
        {approvedSessionsWithoutOrder.length === 0 ? (
          <div className="border-2 border-dashed border-[#1A3626] p-6 text-sm text-[#1A3626]/70">
            All approved sessions are queued. The auto-Shopify-webhook hookup
            will go live in the next deploy — for now you can queue them
            manually after customer checkout.
          </div>
        ) : (
          <ul className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {approvedSessionsWithoutOrder.slice(0, 12).map((s) => (
              <li
                key={s.id}
                className="border-2 border-[#1A3626] bg-white p-2"
                data-testid={`unqueued-session-${s.id}`}
              >
                {s.preview_url && (
                  <img
                    src={fileUrl(s.preview_url)}
                    alt="approved session"
                    className="aspect-square w-full object-cover"
                  />
                )}
                <button
                  onClick={() => onQueue(s.id)}
                  className="mt-2 w-full border-2 border-[#1A3626] px-2 py-1.5 text-[10px] font-black uppercase tracking-[0.25em] hover:bg-[#1A3626] hover:text-[#F9F9F4]"
                  data-testid={`queue-session-${s.id}`}
                >
                  Queue for fulfilment
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Fulfilment queue */}
      <div>
        <div className="font-display mb-4 text-[10px] font-bold uppercase tracking-[0.4em] text-[#1A3626]/65">
          Fulfilment queue ({orders.length})
        </div>
        {orders.length === 0 ? (
          <div className="border-2 border-dashed border-[#1A3626] p-6 text-sm text-[#1A3626]/70">
            No personalised orders queued yet.
          </div>
        ) : (
          <ul className="space-y-3">
            {orders.map((o) => {
              const sess = sessionById[o.session_id];
              return (
                <li
                  key={o.id}
                  className="grid grid-cols-1 gap-4 border-2 border-[#1A3626] bg-white p-4 md:grid-cols-[120px_1fr]"
                  data-testid={`personalisation-order-${o.id}`}
                >
                  {sess?.preview_url ? (
                    <img
                      src={fileUrl(sess.preview_url)}
                      alt="order preview"
                      className="h-28 w-28 border-2 border-[#1A3626] object-cover"
                    />
                  ) : (
                    <div className="flex h-28 w-28 items-center justify-center border-2 border-[#1A3626] bg-[#EBE7DD] text-[10px] uppercase tracking-[0.25em] text-[#1A3626]/60">
                      No preview
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="font-display text-base font-black uppercase tracking-tight">
                      {sess?.shopify_product_id || o.shopify_product_id}
                    </div>
                    <div className="mt-1 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-[#1A3626]/70">
                      <div>Status: <strong className="font-display uppercase tracking-[0.2em]">{o.status}</strong></div>
                      <div>Shopify order: {o.shopify_order_id || "—"}</div>
                      <div>Customer: {o.customer_name || "—"}</div>
                      <div>
                        Printify upload ID:{" "}
                        <span className="font-mono">
                          {o.printify_upload_id || "—"}
                        </span>
                      </div>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {o.status === "queued" && (
                        <button
                          onClick={() => onUpdateStatus(o, "approved")}
                          className="font-display border-2 border-[#1A3626] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.25em] hover:bg-[#1A3626] hover:text-[#F9F9F4]"
                          data-testid={`order-approve-${o.id}`}
                        >
                          QC Approve
                        </button>
                      )}
                      {o.status === "queued" && (
                        <button
                          onClick={() => onUpdateStatus(o, "rejected")}
                          className="font-display border-2 border-red-700 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.25em] text-red-700 hover:bg-red-700 hover:text-white"
                          data-testid={`order-reject-${o.id}`}
                        >
                          Reject
                        </button>
                      )}
                      {o.status === "approved" && !o.printify_upload_id && (
                        <button
                          onClick={() => onPush(o)}
                          className="font-display border-2 border-[#1A3626] bg-[#1A3626] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.25em] text-[#F9F9F4] hover:bg-transparent hover:text-[#1A3626]"
                          data-testid={`order-push-${o.id}`}
                        >
                          Push to Printify
                        </button>
                      )}
                      {o.printify_upload_preview_url && (
                        <a
                          href={o.printify_upload_preview_url}
                          target="_blank"
                          rel="noreferrer"
                          className="font-display border-2 border-[#93C065] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.25em] text-[#1A3626] hover:bg-[#93C065]"
                          data-testid={`order-printify-preview-${o.id}`}
                        >
                          View on Printify
                        </a>
                      )}
                      {o.status === "uploaded" && (
                        <button
                          onClick={() => onUpdateStatus(o, "pushed")}
                          className="font-display border-2 border-[#1A3626] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.25em] hover:bg-[#1A3626] hover:text-[#F9F9F4]"
                          data-testid={`order-mark-pushed-${o.id}`}
                        >
                          Mark pushed
                        </button>
                      )}
                      <button
                        onClick={() => onDelete(o)}
                        className="font-display border-2 border-[#1A3626]/40 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.25em] text-[#1A3626]/50 hover:bg-[#1A3626]/10"
                        data-testid={`order-delete-${o.id}`}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}


/* =====================================================================
   CONCIERGE QUEUE — customer submissions awaiting hand-crafted proof
   ===================================================================== */
function ConciergeQueue({ sessions, onChange }) {
  const STATUS_LABELS = {
    awaiting_admin_proof: "Awaiting proof",
    proof_ready: "Proof ready",
    approved: "Approved by customer",
    pending: "Pending",
    failed: "Failed",
  };
  // Sort: awaiting_admin_proof first (most urgent), then proof_ready, then approved
  const ORDER = ["awaiting_admin_proof", "proof_ready", "approved"];
  const sorted = [...sessions].sort((a, b) => {
    const ai = ORDER.indexOf(a.status);
    const bi = ORDER.indexOf(b.status);
    if (ai !== bi) return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    return new Date(b.created_at) - new Date(a.created_at);
  });

  const previewLink = (s) =>
    `${window.location.origin}/preview/${s.id}`;

  const uploadProof = async (s, file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Pick an image file");
      return;
    }
    try {
      const fd = new FormData();
      fd.append("proof_image", file);
      await api.post(
        `/admin/personalisation-sessions/${s.id}/upload-proof`,
        fd,
        {
          headers: {
            ...adminHeaders(),
            "Content-Type": "multipart/form-data",
          },
          timeout: 60000,
        }
      );
      toast.success("Proof uploaded.");
      await onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    }
  };

  const sendProof = async (s) => {
    try {
      toast.loading("Sending proof…", { id: `send-${s.id}` });
      await api.post(
        `/admin/personalisation-sessions/${s.id}/send-proof`,
        null,
        { headers: adminHeaders(), timeout: 30000 }
      );
      toast.success("Proof sent to customer.", { id: `send-${s.id}` });
      await onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed", {
        id: `send-${s.id}`,
      });
    }
  };

  const copyLink = async (s) => {
    try {
      await navigator.clipboard.writeText(previewLink(s));
      toast.success("Proof link copied — paste into email/WhatsApp.");
    } catch (e) {
      window.prompt("Copy this link:", previewLink(s));
    }
  };

  const deleteSession = async (s) => {
    if (!window.confirm("Delete this customer submission permanently?")) return;
    try {
      await api.delete(`/admin/personalisation-sessions/${s.id}`, {
        headers: adminHeaders(),
      });
      toast.success("Deleted.");
      await onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div data-testid="concierge-queue">
      <div className="font-display mb-4 text-[10px] font-bold uppercase tracking-[0.4em] text-[#1A3626]/65">
        Concierge queue ({sessions.length})
      </div>
      {sessions.length === 0 ? (
        <div className="border-2 border-dashed border-[#1A3626] p-6 text-sm text-[#1A3626]/70">
          No customer submissions yet. When customers tap &ldquo;Personalise
          This Tee&rdquo;, their photo + email lands here for you to
          hand-craft a proof.
        </div>
      ) : (
        <ul className="space-y-4">
          {sorted.map((s) => (
            <ConciergeRow
              key={s.id}
              session={s}
              statusLabel={STATUS_LABELS[s.status] || s.status}
              previewLink={previewLink(s)}
              onUploadProof={(file) => uploadProof(s, file)}
              onSendProof={() => sendProof(s)}
              onCopyLink={() => copyLink(s)}
              onDelete={() => deleteSession(s)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function ConciergeRow({
  session: s,
  statusLabel,
  previewLink,
  onUploadProof,
  onSendProof,
  onCopyLink,
  onDelete,
}) {
  const isAwaiting = s.status === "awaiting_admin_proof";
  const isReady = s.status === "proof_ready";
  const isApproved = s.status === "approved";

  return (
    <li
      className="grid grid-cols-1 gap-4 border-2 border-[#1A3626] bg-white p-4 md:grid-cols-[110px_110px_1fr]"
      data-testid={`concierge-row-${s.id}`}
    >
      {/* Customer face */}
      <div>
        <div className="font-display mb-2 text-[9px] font-bold uppercase tracking-[0.25em] text-[#1A3626]/55">
          Customer photo
        </div>
        {s.customer_face_url ? (
          <a
            href={fileUrl(s.customer_face_url)}
            target="_blank"
            rel="noreferrer"
            data-testid={`row-face-${s.id}`}
          >
            <img
              src={fileUrl(s.customer_face_url)}
              alt="Customer face"
              className="aspect-square w-full border-2 border-[#1A3626] bg-[#EBE7DD] object-contain"
            />
          </a>
        ) : (
          <div className="flex aspect-square w-full items-center justify-center bg-[#EBE7DD] text-[10px] uppercase tracking-[0.25em] text-[#1A3626]/60">
            —
          </div>
        )}
      </div>

      {/* Proof preview */}
      <div>
        <div className="font-display mb-2 text-[9px] font-bold uppercase tracking-[0.25em] text-[#1A3626]/55">
          Proof
        </div>
        {s.preview_url ? (
          <a
            href={fileUrl(s.raw_ai_url || s.preview_url)}
            target="_blank"
            rel="noreferrer"
            data-testid={`row-proof-${s.id}`}
          >
            <img
              src={fileUrl(s.preview_url)}
              alt="Proof"
              className="aspect-square w-full border-2 border-[#1A3626] bg-[#EBE7DD] object-contain"
            />
          </a>
        ) : (
          <div className="flex aspect-square w-full items-center justify-center border-2 border-dashed border-[#1A3626] bg-[#EBE7DD] text-[10px] uppercase tracking-[0.25em] text-[#1A3626]/60">
            None yet
          </div>
        )}
      </div>

      {/* Details + actions */}
      <div className="flex flex-col">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`font-display border-2 px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.25em] ${
              isAwaiting
                ? "border-red-700 bg-red-50 text-red-700"
                : isReady
                ? "border-[#93C065] bg-[#EBE7DD] text-[#1A3626]"
                : isApproved
                ? "border-[#1A3626] bg-[#1A3626] text-[#F9F9F4]"
                : "border-[#1A3626]/40 text-[#1A3626]/60"
            }`}
          >
            {statusLabel}
          </span>
          <span className="text-[10px] uppercase tracking-[0.2em] text-[#1A3626]/50">
            {new Date(s.created_at).toLocaleString()}
          </span>
        </div>

        <div className="mt-3 grid grid-cols-1 gap-x-4 gap-y-1 text-xs text-[#1A3626]/80 sm:grid-cols-2">
          <div>
            <span className="font-display uppercase tracking-[0.2em] text-[#1A3626]/55">
              Customer
            </span>
            <div className="text-sm">
              {s.customer_name || "—"}
              {s.customer_email && (
                <>
                  {" · "}
                  <a
                    href={`mailto:${s.customer_email}`}
                    className="underline-offset-2 hover:underline"
                  >
                    {s.customer_email}
                  </a>
                </>
              )}
            </div>
          </div>
          <div>
            <span className="font-display uppercase tracking-[0.2em] text-[#1A3626]/55">
              Product
            </span>
            <div className="text-sm">{s.shopify_product_id}</div>
          </div>
        </div>

        {s.custom_instructions && (
          <div className="mt-3 border-l-4 border-[#93C065] pl-3 text-xs leading-relaxed text-[#1A3626]/80">
            <span className="font-display uppercase tracking-[0.2em] text-[#1A3626]/55">
              Customer notes
            </span>
            <div className="mt-0.5">{s.custom_instructions}</div>
          </div>
        )}

        {s.proof_sent_at && (
          <div className="mt-3 text-[11px] uppercase tracking-[0.22em] text-[#93C065]">
            Proof emailed {new Date(s.proof_sent_at).toLocaleString()}
          </div>
        )}

        <div className="mt-4 flex flex-wrap gap-2">
          <label
            className="font-display cursor-pointer border-2 border-[#1A3626] bg-transparent px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.25em] hover:bg-[#1A3626] hover:text-[#F9F9F4]"
            data-testid={`upload-proof-${s.id}`}
          >
            {s.preview_url ? "Replace proof" : "Upload proof"}
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) onUploadProof(f);
                e.target.value = "";
              }}
            />
          </label>
          <button
            onClick={onSendProof}
            disabled={!s.preview_url || !s.customer_email}
            className="font-display border-2 border-[#1A3626] bg-[#1A3626] px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.25em] text-[#F9F9F4] hover:bg-transparent hover:text-[#1A3626] disabled:opacity-40"
            data-testid={`send-proof-${s.id}`}
          >
            {s.proof_sent_at ? "Resend proof" : "Send to customer"}
          </button>
          <button
            onClick={onCopyLink}
            className="font-display border-2 border-[#1A3626] px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.25em] hover:bg-[#1A3626] hover:text-[#F9F9F4]"
            data-testid={`copy-link-${s.id}`}
          >
            Copy proof link
          </button>
          <a
            href={previewLink}
            target="_blank"
            rel="noreferrer"
            className="font-display border-2 border-dashed border-[#1A3626] px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.25em] hover:bg-[#1A3626] hover:text-[#F9F9F4]"
            data-testid={`open-link-${s.id}`}
          >
            Open
          </a>
          <button
            onClick={onDelete}
            className="font-display border-2 border-[#1A3626]/40 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.25em] text-[#1A3626]/50 hover:bg-[#1A3626]/10"
            data-testid={`delete-session-${s.id}`}
          >
            Delete
          </button>
        </div>
      </div>
    </li>
  );
}
